import React, { useEffect, useState } from 'react'
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Hidden, IconButton, InputLabel, MenuItem, Select, useTheme } from '@mui/material';
import moment from 'moment';
import useMediaQuery from '@mui/material/useMediaQuery';

import { IDocument } from '../../types/document';

import { DocumentService } from '../../services/documentService';
import { UtilityService } from '../../services/utilityService';
import { useUserContext } from '../../contexts/userContext';

import { Icons } from '../icons';
import Spinner from '../spinner';
import DocumentAdd from '../routes/management/documentAdd';
import { AppConfig } from '../../config/appConfig';

export interface IDocumentTableProps {
	data: IDocument[] | undefined;
	entityId?: string;
	entityTypeId?: string;
	hideAddDocumentsLink?: boolean;
	hideDeleteDocumentsLink?: boolean
	allowExpiringDocuments?: boolean;
	isLoading?: boolean;
	onDocumentDeleted: (id: number) => void;
	onRequestListRefresh: () => void;
	onViewDocument: (documentId: number) => void;
}

const DocumentTable: React.FunctionComponent<IDocumentTableProps> = ({ data, entityId, entityTypeId, hideAddDocumentsLink = true, hideDeleteDocumentsLink, allowExpiringDocuments = true, onRequestListRefresh, onViewDocument, onDocumentDeleted }) => {
	const [workingData, setWorkingData] = useState<IDocument[]>([]);
	const [dataView, setDataView] = useState<IDocument[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.documentListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [itemToDelete, setItemToDelete] = useState<IDocument | undefined>(undefined);
	const [itemToDisplay, setItemToDisplay] = useState<IDocument | undefined>(undefined); // To keep the delete dialog from blanking out text during its fade-out animation
	const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [entityTypeFilter, setEntityTypeFilter] = useState("");
	const [entityTypeFilterOptions, setEntityTypeFilterOptions] = useState<string[]>([]);
	const [entityFilter, setEntityFilter] = useState("");
	const [entityFilterOptions, setEntityFilterOptions] = useState<string[]>([]);
	const [documentTypeFilter, setDocumentTypeFilter] = useState("");
	const [documentTypeFilterOptions, setDocumentTypeFilterOptions] = useState<string[]>([]);
	const [documentStatusFilter, setDocumentStatusFilter] = useState("");
	const [documentStatusFilterOptions, setDocumentStatusFilterOptions] = useState<string[]>(["All", "Active", "Expired"]);

	const [showAddDocumentForm, setShowAddDocumentForm] = useState<boolean>(false);
	const [showDeleteDocumentLink, setShowDeleteDocumentLink] = useState<boolean>(false);

	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	const userContext = useUserContext();

	useEffect(() => {
		if (userContext) {
			// Override Delete permission visibiity if a preference is passed in
			setShowDeleteDocumentLink((hideDeleteDocumentsLink === true) || (hideDeleteDocumentsLink === false) ? !hideDeleteDocumentsLink : userContext.permissions?.canDeleteDocuments ?? false);
		}
	}, [userContext, hideDeleteDocumentsLink]);

	useEffect(() => {
		if (data != undefined) {
			setWorkingData([...data]);
			setIsLoading(false);
		}
	}, [data]);

	useEffect(() => {
		if (workingData) {
			let et: string[] = [];
			let e: string[] = [];
			let dt: string[] = [];

			let temp = workingData;

			temp.map((item) => {
				if (item.entityTypeName && !et.includes(item.entityTypeName)) {
					et.push(item.entityTypeName);
				}

				if (item.entityName && !e.includes(item.entityName)) {
					e.push(item.entityName);
				}

				if (item.documentTypeName && !dt.includes(item.documentTypeName)) {
					dt.push(item.documentTypeName);
				}
			});

			setEntityTypeFilterOptions(et.sort());
			setEntityFilterOptions(e.sort());
			setDocumentTypeFilterOptions(dt.sort());
		}
	}, [workingData])

	useEffect(() => {
		const todaysDate = parseInt(moment().format("yyyyMMDD"));

		if (workingData) {
			let newData = workingData.filter((item) => {
				if (entityTypeFilter && entityTypeFilter != "All" && item.entityTypeName != entityTypeFilter) {
					return false;
				}
				if (entityFilter && entityFilter != "All" && item.entityName != entityFilter) {
					return false;
				}
				if (documentTypeFilter && documentTypeFilter != "All" && item.documentTypeName != documentTypeFilter) {
					return false;
				}
				if (documentStatusFilter && documentStatusFilter != "All" && item.status != documentStatusFilter) {
					return false;
				}

				return true;
			}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}

			if (sortField) {
				switch (sortField) {
					case "entity": {
						newData.sort((a, b) => UtilityService.compareStrings(a.entityName, b.entityName));
						break;
					}
					case "documenttype": {
						newData.sort((a, b) => UtilityService.compareStrings(a.documentTypeName, b.documentTypeName));
						break;
					}
					case "documentname": {
						newData.sort((a, b) => UtilityService.compareStrings(a.name, b.name));
						break;
					}
					case "udate": {
						newData.sort((a, b) => UtilityService.compareStrings(a.uploadDate?.toString(), b.uploadDate?.toString()));
						break;
					}
					case "edate": {
						newData.sort((a, b) => UtilityService.compareStrings(a.expirationDate?.toString(), b.expirationDate?.toString()));
						break;
					}
					case "status": {
						newData.sort((a, b) => UtilityService.compareStrings(a.status?.toString(), b.status?.toString()));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.entityTypeName, b.entityTypeName));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
		}
	}, [workingData, entityTypeFilter, entityFilter, documentTypeFilter, documentStatusFilter, sortField, sortAscending])

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	const handleDelete = () => {
		if (isDeleting) return;
		if (itemToDelete == undefined) return;

		setErrorMessage(undefined);
		setIsDeleting(true);
		DocumentService.deleteDocument(itemToDelete.id)
			.then(() => {
				console.log('DocumentService.deleteDocument: SUCCESS');
				setIsLoading(true);
				onDocumentDeleted?.(itemToDelete.id);
			})
			.catch((error) => {
				console.log('DocumentService.deleteDocument: ERROR', error);
				setErrorMessage("We encountered an error while deleting the document. Please try again.");
			})
			.finally(() => {
				setIsDeleting(false);
				setItemToDelete(undefined);
			});
	}

	const openDeleteDialog = (document: IDocument) => {
		if (!document) return;
		setItemToDelete(document);
		setItemToDisplay(document);
		setShowDeleteDialog(true);
	}

	const handleDeleteDialogClosed = (deleteConfirmed: boolean) => {
		setShowDeleteDialog(false);

		// Dismissing the dialog without clicking one of the buttons returns something other than a boolean
		if ((typeof (deleteConfirmed) == "boolean") && deleteConfirmed) {
			handleDelete();
		}
		else {
			setItemToDelete(undefined);
		}
	}

	return (
		<>
			{showAddDocumentForm ? (
				<section>
					<DocumentAdd
						entityId={entityId}
						entityTypeId={entityTypeId}
						allowExpiringDocuments={allowExpiringDocuments}
						hideFormCallback={(document) => { setShowAddDocumentForm(false); if (document) { setIsLoading(true); onRequestListRefresh?.(); } }}
					></DocumentAdd>
				</section>
			) : (
				<section className="hwi-entity-search-results-table">
					<div className="search">
						{!entityId &&
							<>
								<FormControl size="small">
									<InputLabel>Entity Type</InputLabel>
									<Select
										value={entityTypeFilter}
										defaultValue="All"
										label="Entity Type"
										onChange={(event) => { setEntityTypeFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{entityTypeFilterOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small">
									<InputLabel>Entity</InputLabel>
									<Select
										value={entityFilter}
										defaultValue="All"
										label="Entity"
										onChange={(event) => { setEntityFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{entityFilterOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
							</>
						}
						<FormControl size="small">
							<InputLabel>Document Type</InputLabel>
							<Select
								value={documentTypeFilter}
								defaultValue="All"
								label="Document Type"
								onChange={(event) => { setDocumentTypeFilter(event.target.value) }}
							>
								<MenuItem value="All" key="-1">All</MenuItem>
								{documentTypeFilterOptions.map((item, index) => (
									<MenuItem value={item} key={index}>{item}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small">
							<InputLabel>Status</InputLabel>
							<Select
								value={documentStatusFilter}
								defaultValue="All"
								label="Status"
								onChange={(event) => { setDocumentStatusFilter(event.target.value) }}
							>
								{documentStatusFilterOptions.map((item, index) => (
									<MenuItem value={item} key={index}>{item}</MenuItem>
								))}
							</Select>
						</FormControl>
						{!hideAddDocumentsLink &&
							<div className="controls">
								<Button variant='contained' color='secondary' onClick={() => { setShowAddDocumentForm(true); onViewDocument?.(undefined); }}>Add Document</Button>
							</div>
						}
					</div>
					{errorMessage &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					}
					<div className="headers">
						{!entityId &&
							<>
								<div className="entity-type-col">
									<p onClick={() => handleHeaderClick("entitytype")}>
										Entity Type
										{" "}
										{(sortField == "entitytype" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
										{(sortField == "entitytype" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
									</p>
								</div>
								<div className="entity-col">
									<p onClick={() => handleHeaderClick("entity")}>
										Entity
										{" "}
										{(sortField == "entity" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
										{(sortField == "entity" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
									</p>
								</div>
							</>
						}
						<div className="document-type-col">
							<p onClick={() => handleHeaderClick("documenttype")}>
								Document Type
								{" "}
								{(sortField == "documenttype" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "documenttype" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="name-col">
							<p onClick={() => handleHeaderClick("documentname")}>
								Document Name
								{" "}
								{(sortField == "documentname" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "documentname" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="uploaded-col">
							<p onClick={() => handleHeaderClick("udate")}>
								Uploaded
								{" "}
								{(sortField == "udate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "udate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="expires-col">
							<p onClick={() => handleHeaderClick("edate")}>
								Expires
								{" "}
								{(sortField == "edate" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "edate" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="status-col">
							<p onClick={() => handleHeaderClick("status")}>
								Status
								{" "}
								{(sortField == "status" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
								{(sortField == "status" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
							</p>
						</div>
						<div className="controls-col">{" "}</div>
					</div>
					<Spinner dependency={isLoading} text="Loading Documents...">
						<div className="data">
							{dataView && dataView?.length > 0 ?
								(
									dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
										<div className={"row"} key={item.id}>
											{!entityId &&
												<>
													<div className="entity-type-col"><p>{item.entityTypeName}</p></div>
													<div className="entity-col"><p>{item.entityName}</p></div>
												</>
											}
											<div className="document-type-col">
												<p>
													{item.documentTypeName}
													{item.isSensitive &&
														<>
															<br />
															<span className="sensitive">(Sensitive)</span>
														</>
													}
												</p>
											</div>
											<div className="name-col"><p>{item.name}</p></div>
											<div className="uploaded-col"><p>{moment(item.uploadDate).format("MM/DD/yyyy")}</p></div>
											<div className="expires-col"><p>{item.expirationDate ? moment(item.expirationDate).format("MM/DD/yyyy") : "Never"}</p></div>
											<div className="status-col"><p>{item.status}</p></div>
											<div className="controls-col">
												{item?.id != undefined &&
													<p onClick={() => onViewDocument?.(item.id!)}>
														{!(isDeleting && item.id == itemToDelete?.id) && (<><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>{" "}View</>)}
													</p>
												}
												{" "}
												{item?.id != undefined && showDeleteDocumentLink &&
													<p
														onClick={() => openDeleteDialog(item)}
														className={`
															${(isDeleting && item.id != itemToDelete?.id) ? "blocked" : ""} 
															${(isDeleting) ? "progresscursor" : ""}`}>
														{!isDeleting && (<><Icons.RemoveItem fill="#a82525" height="14px"></Icons.RemoveItem>{" "}<span className='delete'>Delete</span></>)}
														{isDeleting && item.id != itemToDelete?.id && (<><Icons.RemoveItem fill="#a82525" height="14px"></Icons.RemoveItem>{" "}<span className='delete'>Delete</span></>)}
														{isDeleting && item.id == itemToDelete?.id && (<>Deleting...</>)}
													</p>
												}
											</div>
										</div>
									))
								) : (<div className="no-results">0 results</div>)}
						</div>
						{dataView?.length > 0 &&
							<div className="pager">
								<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
								<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
								<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
							</div>
						}
					</Spinner>
					{/* <Json data={entityId} title="Entity:"></Json> */}
					{/* <Json data={entityTypeId} title="Entity Type:"></Json> */}
				</section>
			)}
			<Dialog
				fullScreen={fullScreen}
				open={showDeleteDialog}
				onClose={handleDeleteDialogClosed}
				aria-labelledby='delete-confirmation-title'
				className='delete-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle
					id="delete-confirmation-title">
					{"Delete Document"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<p>Are you sure you want to delete this {itemToDisplay?.documentTypeName ?? "document"}? </p>
						<p>This action cannot be undone.</p>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button className='delete-cancel' onClick={() => handleDeleteDialogClosed(false)}>
						Cancel
					</Button>
					<Button className='delete-ok' onClick={() => handleDeleteDialogClosed(true)}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default DocumentTable;